import axios from 'axios';
import Token from '~/api/Token';
import RestX from 'highered-utils/restx';
import DialogService from './DialogService';
import IntercomService from './IntercomService';
import Router from '~/router';

import GoogleFlow from '~/utils/socialAuth/GoogleFlow';
import FacebookFlow from '~/utils/socialAuth/FacebookFlow';
import LinkedInFlow from '~/utils/socialAuth/LinkedInFlow';
import WeChatFlow from '~/utils/socialAuth/WeChatFlow';

import UserConsentContext from '~/services/UserConsentContext';
import OrganizationService from './OrganizationService';
import IdentityService from './IdentityService';
import CandidateService from './CandidateService';
import BackgroundProcesses from './BackgroundProcessesService';
import { SchoolVerificationState } from './SchoolService';
import ChatService from '~/services/ChatService';
import store from '~/store';

class AuthService {
    // Gets the userId. Returns a promise with null if not logged in or on failure.
    static getUserId() {
        // If not logged in, there is no user-id, return null
        if (!this.isLoggedIn()) {
            return Promise.resolve(null);
        }

        const user = RestX.getInterfaces().profileModule.getUser();
        if (user && user.id) {
            return Promise.resolve(user.id);
        }

        // Fetch the user data and return the id
        // This is most likely cached
        return RestX.getInterfaces()
            .user.me.get()
            .then(data => {
                data = data.data || data;
                if (data.id) {
                    return data.id;
                }
                return null;
            })
            .catch(error => {
                console.log(error);
                return null;
            });
    }

    static isAuthenticated() {
        return Token.getAccess() !== undefined;
    }

    static isLoggedIn() {
        return RestX.getInterfaces().logInModule.isLoggedIn();
    }

    static async setIsLoggedIn(value) {
        if (value) {
            this.onLogIn();
        }
        return RestX.getInterfaces().logInModule.setIsLoggedIn(value);
    }

    static async onLogIn() {
        console.debug('Logging in');
        return AuthService.loadUserData().then(async data => {
            if (!UserConsentContext.hasAnswered()) UserConsentContext.acceptAll(); // Accept all user consents if not answered

            BackgroundProcesses.scheduleVirtualEventPromotion();

            // Fetch global and school widgets
            const orgService = new OrganizationService();
            orgService.getGlobalWidgets().then(widgets => {
                RestX.getInterfaces().widgetModule.setGlobalWidgets(widgets);
            });

            const chatService = new ChatService();
            await chatService.init();
            store.dispatch('talkSessionModule/updateChatService', chatService);

            RestX.getInterfaces()
                .university.connection.get({})
                .then(it => {
                    const verified = it.data.filter(
                        c => c.state === SchoolVerificationState.verified
                    );
                    if (verified.length > 0) {
                        orgService.getSchoolWidgets(verified.map(c => c.schoolId)).then(widgets => {
                            RestX.getInterfaces().widgetModule.setSchoolWidgets(widgets);
                        });
                    }
                });

            return data;
        });
    }

    static async loadUserData() {
        const interfaces = RestX.getInterfaces();
        await interfaces.user.me.get(); // Gets basic user data
        await interfaces.university.connection.get({
            forceUpdate: true,
        }); // Get university connections

        await AuthService.loadUserProfile();
    }

    static async loadUserProfile() {
        const interfaces = RestX.getInterfaces();
        const candidateService = new CandidateService();
        return Promise.all([
            new IdentityService().getMe().then(user => {
                interfaces.profileModule.setUser(user);
                return user;
            }),
            candidateService.getMe().then(candidate => {
                interfaces.profileModule.setCandidate(candidate);
                return candidate;
            }),
            candidateService.getWorkAuthorizations().then(wa => {
                interfaces.profileModule.setWorkAuthorizations(wa);
                return wa;
            }),
            candidateService.getLastProfileImport().then(ir => {
                interfaces.profileModule.setLastProfileImport(ir);
                return ir;
            }),
        ]);
    }

    static logOut() {
        console.debug('Logging out');
        Token.remove();
        RestX.getInterfaces().logInModule.setIsLoggedIn(false);
        RestX.getInterfaces().user.clear();
        RestX.getInterfaces().profile.clear();
        RestX.getInterfaces().university.clear();
        RestX.getInterfaces().candidate.clear();
        RestX.getInterfaces().videoCallModule.clear();
        RestX.getInterfaces().profileModule.clear();
        RestX.getInterfaces().widgetModule.clear();

        IntercomService.reset();

        RestX.getInterfaces().logInModule.setOpen(true);

        // Reload page at /
        Router.push({ path: '/' });
        Router.go(0);
    }

    // Authenticates with an authorization code recieved from an external token
    static async authenticate(authCode, provider, state) {
        if (!authCode) {
            return Promise.reject();
        }

        // Validate provider
        let accessTokenPromise = null;
        switch (provider) {
            case 'Google':
                accessTokenPromise = GoogleFlow.getAccessToken(authCode, state);
                break;
            case 'Facebook':
                accessTokenPromise = FacebookFlow.getAccessToken(authCode, state);
                break;
            case 'LinkedIn':
                accessTokenPromise = LinkedInFlow.getAccessToken(authCode, state);
                break;
            case 'WeChat':
                accessTokenPromise = WeChatFlow.getAccessToken(authCode, state);
                break;
            default:
                return Promise.reject();
        }

        // Get access token and refresh token
        return accessTokenPromise
            .then(res => res.data)
            .then(async tokens => {
                if (tokens && tokens.accessToken) {
                    // Set tokens
                    const accessToken = tokens.accessToken;
                    const refreshToken = tokens.refreshToken;
                    Token.setAccess(accessToken);
                    Token.setRefresh(refreshToken);

                    // Initialize log in
                    this.setIsLoggedIn(true).then(() => {
                        IntercomService.setUserData(IntercomService.SocialAuthLogin(provider));
                    });
                }
                return tokens;
            })
            .catch(err => {
                err = err.response ? err.response.data : err;
                const error = err instanceof Array && err.length > 0 ? err[0] : err;

                // Check for 'invalid provider' code, which means the user tried to authenticate with wrong provider.
                if (error.code === 'InvalidProvider') {
                    // Using 'setTimeout', so that the error dialog is shown above the log in dialog.
                    setTimeout(() => {
                        DialogService.openErrorDialog(error.description);
                    }, 500);
                }
                return err;
            });
    }

    static register(data) {
        const urlParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlParams);
        return axios({
            method: 'POST',
            url: `${process.env.VUE_APP_IDENTITY_SERVER_API_URL}api/v1/identity/user`,
            data: {
                ...data,
                metadata: params,
            },
            params,
        });
    }
}

export default AuthService;
